/**
 * Free-text capture box for WhatsApp or in-person developments (ticket
 * body): stored as a user-action record with actor and time, merged by the
 * next run.
 *
 * This lists every capture (note,
 * actor, time) beneath the form, each with its own Undo, and a freshly
 * submitted note appears immediately via `useHelmActions`'s optimistic
 * append rather than waiting for the next poll.
 *
 * A7Y-5210: when HELM's record is reachable, an account can be picked; the
 * note is then a dated history line on that account (`appendCrmEvent`), not a
 * HELM capture, and the box says where it went.
 */
import { useState } from 'react';
import type React from 'react';
import { Button, Select, Textarea } from '@a7y/tailwind-catalyst';
import type { HelmCapture } from '@a7y/platform-health/helm';
import { HelmSection } from './HelmSection';

/** A capture's key is `capture:<actionId>` (plan §2.2) — this is the only
 * place on the page an undo target is derived from a key rather than
 * carried on the item directly. */
function actionIdFromCaptureKey(key: string): string {
  return key.slice('capture:'.length);
}

export interface CaptureAccount {
  id: string;
  name: string;
}

export function CaptureBox({
  captures,
  submitting,
  onSubmit,
  onUndo,
  accounts = null,
  onSubmitToAccount,
}: {
  captures: HelmCapture[];
  submitting: boolean;
  onSubmit: (note: string) => void;
  onUndo: (actionId: string) => void;
  /** The record's accounts, or null when the record is not reachable (no picker). */
  accounts?: CaptureAccount[] | null;
  /** Resolves true once the history line is written. */
  onSubmitToAccount?: (accountId: string, note: string) => Promise<boolean>;
}) {
  const [note, setNote] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const picker = accounts !== null && accounts.length > 0 && onSubmitToAccount !== undefined;
  const sorted = picker ? [...accounts].sort((a, b) => a.name.localeCompare(b.name)) : [];

  return (
    <HelmSection title="Capture" lineOn="none">
      <Textarea
        rows={2}
        placeholder="WhatsApp or in-person development…"
        value={note}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {picker && (
          <Select
            aria-label="Account for this note"
            data-testid="helm-capture-account"
            value={accountId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAccountId(e.target.value)}
            className="max-w-xs"
          >
            <option value="">No account: keep it in HELM</option>
            {sorted.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        )}
        <Button
          data-testid="helm-capture-submit"
          disabled={submitting || note.trim().length === 0}
          onClick={() => {
            const trimmed = note.trim();
            if (!trimmed) return;
            setSaved(null);
            const picked =
              picker && accountId !== '' ? sorted.find((a) => a.id === accountId) : undefined;
            if (picked && onSubmitToAccount) {
              void onSubmitToAccount(picked.id, trimmed).then((ok) => {
                if (!ok) return;
                setSaved(`Saved to ${picked.name}'s history.`);
                setNote('');
                setAccountId('');
              });
              return;
            }
            onSubmit(trimmed);
            setNote('');
          }}
        >
          Capture
        </Button>
        {saved && (
          <span data-testid="helm-capture-saved" className="text-xs text-zinc-500">
            {saved}
          </span>
        )}
      </div>

      {captures.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-zinc-100 pt-3 text-sm dark:border-zinc-800">
          {captures.map((c) => {
            const actionId = actionIdFromCaptureKey(c.key);
            // Not yet replaced by the real, server-assigned action — briefly
            // true right after submit, before the create response lands.
            const isOptimistic = actionId.startsWith('optimistic-');
            return (
              <li
                key={c.key}
                data-testid="helm-capture-item"
                className="flex items-center justify-between gap-2"
              >
                <span>
                  {c.note}
                  <span className="ml-2 text-xs text-zinc-500">
                    · {c.actor.name || c.actor.email || 'you'} ·{' '}
                    {new Date(c.capturedAt).toLocaleString()}
                  </span>
                </span>
                <Button
                  plain
                  data-testid="helm-capture-undo"
                  disabled={isOptimistic}
                  onClick={() => onUndo(actionId)}
                  className="shrink-0"
                >
                  Undo
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </HelmSection>
  );
}
